import * as THREE from "three";
import { CAMERA_NEAR, CAMERA_FAR } from "./cameraConstants";

const DEG2RAD = Math.PI / 180;

// Whether *something downstream* is still going to run tonemapping + sRGB
// encoding on this frame after our material's own shader runs — true for
// the live viewport (Viewport.jsx's composer chain ends in an OutputPass
// that re-processes the *whole* rendered frame) and temporarily flipped to
// false by renderPipeline.js's renderBeauty() around its own direct,
// composer-less render (which does its own plain linear->sRGB encode
// afterward on the CPU, with no tonemapping step at all — see
// srgbEncodeInPlace). One shared uniform *object*, referenced (not copied)
// by every projected material's shader (see applyProjectionsToMaterial) —
// flipping `.value` here updates every one of them for the very next render,
// with no need to track which materials currently exist.
//
// Why this is needed at all: three.js only bakes per-material tonemapping/
// colorspace-encoding into a shader when it's rendering *directly to the
// canvas* (WebGLPrograms.js gates both on `renderer.getRenderTarget() ===
// null`) — every render this editor does, live viewport or export, goes
// through *some* offscreen target first (the composer's own ping-pong
// buffers, or renderBeauty's explicit WebGLRenderTarget), so that gate is
// always false for our material. What differs between the two cases is
// purely what happens *afterward*: the live viewport's OutputPass reprocesses
// the frame with ACES tonemapping + sRGB encoding; renderBeauty's own
// afterward step is just a linear->sRGB encode, no tonemapping. Our override
// has to pre-correct for whichever one is actually coming.
export const projectionOutputPassCompensation = { value: true };

// Lets a caller temporarily render the scene as if no projection were
// active, without a scene-graph rebuild — every projected material's shader
// already reads this uniform (see applyProjectionsToMaterial), so flipping
// `.value` here affects every one of them for the very next render, then
// flipping it back restores the normal, projection-visible look. Used by
// renderPipeline.js's runAIRenderPipeline: the "beauty" pass sent to the
// gen-AI model always shows the scene's own real materials/lighting, never
// whatever a currently-active projection is showing in the live editor — a
// second, separate render (with this back at its default `true`) is what
// captures the projected look instead, sent as its own "texture reference"
// image (see buildSceneJson's TEXTURE_REFERENCE_NOTE for why that one's
// framed to the model as a guide, not literal truth).
export const projectionEnabled = { value: true };

// Keyed by URL — a projection's texture never changes after creation, and
// buildSceneGraph (sceneGraph.js) rebuilds the whole graph on every node
// edit, so this is what keeps toggling/editing the scene from re-fetching the
// same image over and over. Never disposed, same lifetime convention as
// sceneGraph.js's own modelSceneCache — both are small, editor-session-scoped
// caches, not something under memory pressure.
const textureCache = new Map();

function getProjectionTexture(url) {
  if (!url) return null;
  if (!textureCache.has(url)) {
    const texture = new THREE.TextureLoader().load(url);
    // Left at NoColorSpace (three.js's default) rather than SRGBColorSpace —
    // the fragment-shader override in applyProjectionsToMaterial writes this
    // texture's sampled value straight to gl_FragColor as an unlit
    // passthrough of the render's own finished pixels; decoding it to linear
    // here would just mean immediately re-encoding it back on output, and
    // the round trip through this material's tonemapping isn't wanted for
    // an already-graded image in the first place (see that function).
    //
    // Mipmaps disabled: automatic LOD selection for a `texture2DProj` sample
    // is derived from the screen-space derivative of the *projected*
    // (post-divide) coordinate, which can spike sharply even away from a
    // grazing angle — this produced visible banding across otherwise flat,
    // camera-facing surfaces. A single non-mipmapped bilinear sample has no
    // LOD to get wrong.
    texture.generateMipmaps = false;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    textureCache.set(url, texture);
  }
  return textureCache.get(url);
}

// One throwaway camera, reused across calls — every result (matrix/position
// snapshots, or a depth render that reads its state synchronously) is
// resolved before the next call can touch it, so sharing this instance is
// safe. Never added to any scene, never itself rendered.
const projectorCamera = new THREE.PerspectiveCamera(
  50,
  1,
  CAMERA_NEAR,
  CAMERA_FAR,
);

// Points the shared projector camera at a projection record's saved pose —
// the camera itself never moves again once a projection exists (its whole
// point is to keep looking right from the angle it was captured at, even
// after the user later moves the real camera node — see Scene3D.js's
// projectionSchema), so callers only need this when the *projection* changes
// (buildProjectorMatrix) or when re-rendering its occlusion depth map against
// current geometry (renderProjectorDepth) — never per live-viewport frame.
function positionProjectorCamera({ position, rotation, fov, aspect }) {
  projectorCamera.fov = fov || 50;
  projectorCamera.aspect = aspect || 16 / 9;
  projectorCamera.updateProjectionMatrix();
  projectorCamera.position.set(position.x, position.y, position.z);
  projectorCamera.rotation.set(
    (rotation?.x || 0) * DEG2RAD,
    (rotation?.y || 0) * DEG2RAD,
    (rotation?.z || 0) * DEG2RAD,
  );
  projectorCamera.updateMatrixWorld(true);
  return projectorCamera;
}

function buildProjectorMatrix(pose) {
  const camera = positionProjectorCamera(pose);
  const viewProjectionMatrix = new THREE.Matrix4().multiplyMatrices(
    camera.projectionMatrix,
    camera.matrixWorldInverse,
  );
  return {
    viewProjectionMatrix,
    cameraWorldPosition: camera.position.clone(),
  };
}

const backdropForward = new THREE.Vector3();

// Turns a saved projection record's pose (Scene3D.js's projectionSchema
// shape — the same one renderProjectorDepth/resolveProjectionDescriptor
// each take as `projection`) into the flat {position, rotation, fov, aspect}
// positionProjectorCamera itself expects.
function poseFromProjection(rawProjection) {
  return {
    position: rawProjection.cameraTransform?.position,
    rotation: rawProjection.cameraTransform?.rotation,
    fov: rawProjection.fov,
    aspect: rawProjection.aspect,
  };
}

// A flat plane, sized and oriented to exactly fill the projector's own
// frustum at `distance`, carrying the same projected-material shader as
// every real mesh (applyProjectionsToMaterial) — so a pixel of the
// projector's captured photo that landed on no real scene geometry shows up
// here instead of just vanishing into the scene's own flat background, the
// same way a photo backdrop sits behind a physical model. `distance` is the
// caller's job (sceneGraph.js — it needs the live scene's actual bounds to
// place this beyond everything real, this module has no notion of that);
// callers may reposition an existing backdrop in place via
// repositionProjectionBackdrop rather than rebuilding one from scratch.
export function createProjectionBackdrop(
  rawProjection,
  projectionDescriptors,
  distance,
) {
  if (!projectionDescriptors?.length) return null;
  // The backdrop is a flat plane facing straight back at its OWN projector by
  // construction (see repositionProjectionBackdrop, below) — its true normal
  // is just that projector camera's own local +Z axis in world space. Passed
  // to applyProjectionsToMaterial as a constant so its facing test doesn't
  // need to re-derive this from screen-space derivatives every frame — see
  // that function's own comment on why that's unreliable way out at this
  // material's distance from the world origin. This is a fixed geometric
  // property of the plane itself, reused unchanged across every layer's own
  // facing test below, even though the plane is only "aimed at" one of them.
  const camera = positionProjectorCamera(poseFromProjection(rawProjection));
  const flatNormal = new THREE.Vector3(0, 0, 1).applyQuaternion(
    camera.quaternion,
  );

  const material = new THREE.MeshBasicMaterial({
    color: 0x000000,
    side: THREE.DoubleSide,
    // discardUnpainted below drives real per-pixel alpha (feathered coverage
    // near a projection's frustum/facing edge) rather than a hard on/off —
    // needs blending enabled to actually show the scene background through.
    transparent: true,
  });
  // discardUnpainted: this material's whole purpose is catching pixels any
  // VISIBLE projection covers — anywhere none of them do (outside every
  // frustum, facing away, or occluded), the scene's real background should
  // show through instead of this material's own (otherwise never seen) black
  // base color. Gets the FULL stack of visible descriptors, not just the one
  // this plane is physically oriented for — two projectors' backdrop planes
  // can overlap in screen space, and whichever one wins the ordinary depth
  // test at a given pixel still needs to correctly composite every visible
  // projector's contribution there, the same as any real mesh does.
  applyProjectionsToMaterial(material, projectionDescriptors, {
    flatNormal,
    discardUnpainted: true,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  mesh.userData.isProjectionBackdrop = true;
  repositionProjectionBackdrop(mesh, rawProjection, distance);
  return mesh;
}

// Re-derives a backdrop's size/position/orientation in place — used both
// right after createProjectionBackdrop and again whenever the caller's own
// notion of `distance` changes (sceneGraph.js re-fits it the same moment it
// re-fits shadow cameras: once synchronously per rebuild, and again once an
// async-loading GLB's real bounds are known). Cheaper than disposing and
// rebuilding the whole mesh for what's just a resize.
export function repositionProjectionBackdrop(mesh, rawProjection, distance) {
  const camera = positionProjectorCamera(poseFromProjection(rawProjection));
  const height = 2 * distance * Math.tan((camera.fov * DEG2RAD) / 2);
  const width = height * camera.aspect;
  if (
    mesh.geometry.parameters.width !== width ||
    mesh.geometry.parameters.height !== height
  ) {
    mesh.geometry.dispose();
    mesh.geometry = new THREE.PlaneGeometry(width, height);
  }
  backdropForward.set(0, 0, -1).applyQuaternion(camera.quaternion);
  mesh.position
    .copy(camera.position)
    .addScaledVector(backdropForward, distance);
  mesh.quaternion.copy(camera.quaternion);
}

// Long edge of the occlusion depth map — see renderProjectorDepth. Same
// resolution as a light's own shadow map (DEFAULT_SHADOW_MAP_SIZE in
// sceneGraph.js) even though this re-renders far more often (every scene-graph
// rebuild — every node edit — while any projection is visible, not just when
// a light's own bounds change) — a coarser map left visible silhouette-edge
// artifacts (see applyProjectionsToMaterial's occlusion PCF: a low-res depth
// sample straddling a real object's edge doesn't line up with that object's
// true screen-space silhouette, letting the projected image "bleed" onto the
// backdrop right past the object instead of being cleanly cut off there).
const PROJECTOR_DEPTH_MAP_SIZE = 2048;

// Keyed by projection _id — every simultaneously *visible* projection needs
// its own occlusion depth map (rendered from its own frozen pose), unlike the
// old single-active-projection design which only ever needed one. Module-
// level/session-scoped like textureCache, but unlike that cache this one
// DOES need active pruning: a hidden/deleted projection's entry has nothing
// else that ever revisits it, so it would otherwise leak one
// WebGLRenderTarget + DepthTexture per toggled-off projection for the rest of
// the editor session — see pruneProjectorDepthTargets, called by Viewport.jsx
// after every scene-graph rebuild.
const depthTargets = new Map();

function ensureProjectorDepthTarget(projectionId, aspect) {
  const width =
    aspect >= 1
      ? PROJECTOR_DEPTH_MAP_SIZE
      : Math.round(PROJECTOR_DEPTH_MAP_SIZE * aspect);
  const height =
    aspect >= 1
      ? Math.round(PROJECTOR_DEPTH_MAP_SIZE / aspect)
      : PROJECTOR_DEPTH_MAP_SIZE;
  const existing = depthTargets.get(projectionId);
  if (existing && existing.width === width && existing.height === height) {
    return existing;
  }
  existing?.dispose();
  const target = new THREE.WebGLRenderTarget(width, height);
  target.depthTexture = new THREE.DepthTexture(width, height);
  // Nearest, not the Linear default: bilinear-interpolating either texture
  // right at a real object's silhouette blends a near depth/mask texel with
  // a far one into a value that's wrong for BOTH surfaces, which is exactly
  // what let the projected image visibly "bleed" past an object's edge onto
  // the backdrop behind it — occlusion is a discontinuous test and can't be
  // smoothed by filtering the *inputs*; applyProjectionsToMaterial's own PCF
  // (multi-tap NEAREST samples, averaged) is the correct place to introduce
  // any softness at that boundary instead.
  target.texture.minFilter = THREE.NearestFilter;
  target.texture.magFilter = THREE.NearestFilter;
  target.depthTexture.minFilter = THREE.NearestFilter;
  target.depthTexture.magFilter = THREE.NearestFilter;
  depthTargets.set(projectionId, target);
  return target;
}

// Disposes/removes every depth target whose projection isn't in `activeIds`
// (a Set of currently-visible projection ids) — see this module's own note on
// depthTargets above for why this can't just be left to garbage collection.
export function pruneProjectorDepthTargets(activeIds) {
  for (const [id, target] of depthTargets) {
    if (!activeIds.has(id)) {
      target.dispose();
      depthTargets.delete(id);
    }
  }
}

// A plain, shared depth-only material for the occlusion pre-pass below —
// swapped in via scene.overrideMaterial, so every real per-object material
// (including our own projected ones) is bypassed for that one render call.
// DoubleSide deliberately: occlusion is about whether *any* solid geometry
// sat nearer the projector at this pixel, not which way that geometry's
// winding faces it — with the default FrontSide, a triangle that's a
// backface from the projector's specific angle never wrote depth at all, so
// whatever's actually behind it read a wrong (too-far) stored depth in the
// occlusion test below. Concave, multi-part geometry (a typical imported
// GLB) hits this far more than a single convex primitive.
const depthPrepassMaterial = new THREE.MeshDepthMaterial({
  side: THREE.DoubleSide,
});

// Same hide/restore-visibility pattern as renderPipeline.js's
// withHelpersHidden (not imported from there — that file already imports
// from this one, via projectionOutputPassCompensation, and this module must
// not import anything back from it or the two would form a cycle). Also
// hides `excludeFromProjectorOcclusion`-tagged objects — unlike
// isEditorHelper (gizmos meant to never render for real: also hidden from
// GTAO and the AI beauty pass, see those flag's other consumers), this
// second flag is narrower: a light node's own "Visible in Viewport" marker
// sphere (sceneGraph.js) IS meant to render normally everywhere else (it's
// real content for the AI beauty pass), it just shouldn't behave as a solid
// occluder in the projector's own depth pre-pass below — a small light
// marker sitting anywhere in a projector's frustum would otherwise punch an
// unexplained hole in the projected image exactly at its position.
function withProjectionOccludersHidden(scene, fn) {
  const hidden = [];
  scene.traverse((o) => {
    if (
      (o.userData.isEditorHelper || o.userData.excludeFromProjectorOcclusion) &&
      o.visible
    ) {
      o.visible = false;
      hidden.push(o);
    }
  });
  try {
    fn();
  } finally {
    for (const o of hidden) o.visible = true;
  }
}

// Renders the *current* scene's depth from the frozen projector's pose into
// the same depth texture resolveProjectionDescriptor's materials already
// reference (a stable object — this only repopulates its contents) — what
// applyProjectionsToMaterial's occlusion test compares each fragment against,
// so e.g. a box standing in front of a floor correctly keeps the projected
// image off the floor behind it instead of painting through. Called once per
// scene-graph rebuild (Viewport.jsx) while a projection is active, so
// occlusion always reflects whatever the geometry currently is, even though
// the projector itself is frozen. Must run with scene.overrideMaterial set
// (not the real per-object materials) — those materials' own shaders sample
// this exact texture, and reading a texture that's simultaneously bound as
// the active render target's depth attachment is undefined behavior in
// WebGL.
const previousClearColor = new THREE.Color();

export function renderProjectorDepth({ renderer, scene, projection }) {
  if (!renderer || !scene || !projection) return;
  const aspect = projection.aspect || 16 / 9;
  const target = ensureProjectorDepthTarget(projection._id, aspect);
  const camera = positionProjectorCamera({
    position: projection.cameraTransform?.position,
    rotation: projection.cameraTransform?.rotation,
    fov: projection.fov,
    aspect,
  });

  withProjectionOccludersHidden(scene, () => {
    const previousOverride = scene.overrideMaterial;
    const previousTarget = renderer.getRenderTarget();
    renderer.getClearColor(previousClearColor);
    const previousClearAlpha = renderer.getClearAlpha();
    scene.overrideMaterial = depthPrepassMaterial;
    renderer.setRenderTarget(target);
    // Alpha 0 wherever nothing gets drawn — the target's color attachment
    // (target.texture, exposed as uProjDepthMaskTexture below) doubles as a
    // "was any real geometry actually recorded at this pixel" mask.
    // Distinguishing that from "the depth here is the default far value
    // because nothing solid occupies this ray" matters because the
    // projection backdrop's own far depth (see createProjectionBackdrop) is
    // otherwise numerically indistinguishable, at this camera's near/far
    // range, from that same default — comparing against it produced
    // effectively random per-pixel noise instead of a real answer.
    renderer.setClearColor(0x000000, 0);
    renderer.render(scene, camera);
    renderer.setClearColor(previousClearColor, previousClearAlpha);
    renderer.setRenderTarget(previousTarget);
    scene.overrideMaterial = previousOverride;
  });
}

// Resolves a saved projection record (Scene3D.js's projectionSchema shape)
// into everything applyProjectionsToMaterial needs — the one thing Viewport.jsx
// actually calls, memoized there on the visible-projections array so this
// (and the texture fetch) doesn't rerun on every unrelated scene edit. The
// occlusion depth texture this includes is a stable reference whose contents
// renderProjectorDepth (above) keeps current on every scene-graph rebuild —
// getting a reference to it here doesn't populate it.
export function resolveProjectionDescriptor(projection) {
  if (!projection) return null;
  const texture = getProjectionTexture(projection.textureUrl);
  if (!texture) return null;
  const aspect = projection.aspect || 16 / 9;
  const { viewProjectionMatrix, cameraWorldPosition } = buildProjectorMatrix({
    position: projection.cameraTransform?.position,
    rotation: projection.cameraTransform?.rotation,
    fov: projection.fov,
    aspect,
  });
  const depthProjectorTarget = ensureProjectorDepthTarget(
    projection._id,
    aspect,
  );
  return {
    id: projection._id,
    viewProjectionMatrix,
    cameraWorldPosition,
    texture,
    depthTexture: depthProjectorTarget.depthTexture,
    // The same render's color attachment, alpha-only — see
    // renderProjectorDepth's own comment on why "was anything drawn here at
    // all" needs to be a real, explicit signal rather than inferred from the
    // depth value alone.
    depthMaskTexture: depthProjectorTarget.texture,
    // One occlusion-map texel's size in UV units — applyProjectionsToMaterial's
    // occlusion PCF offsets its extra taps by this, so the kernel's footprint
    // stays exactly one texel wide regardless of this projection's own
    // aspect-dependent target dimensions (see ensureProjectorDepthTarget).
    texelSize: new THREE.Vector2(
      1 / depthProjectorTarget.width,
      1 / depthProjectorTarget.height,
    ),
  };
}

// Plural convenience for the whole visible, z-ordered stack (Viewport.jsx's
// only actual call site) — `.filter(Boolean)` drops any projection whose
// texture hasn't resolved (e.g. still loading/missing url) without breaking
// the rest of the stack's indices apart from each other.
export function resolveProjectionDescriptors(projections) {
  return (projections || [])
    .map((p) => resolveProjectionDescriptor(p))
    .filter(Boolean);
}

// Patches a material (any of MeshStandardMaterial/MeshBasicMaterial/a cloned
// GLB material — sceneGraph.js's buildObject3D calls this for every mesh node
// whenever any projection is visible) so it shows the composited stack of
// projected images instead of its own color/map/lighting, wherever the
// surface both faces a given frozen projector and falls inside its frustum —
// everywhere none of them paint, it falls back to whatever that material
// would normally show. The override writes directly to `gl_FragColor` *after*
// every lighting/tonemapping/colorspace chunk has already run (anchored on
// `#include <dithering_fragment>`, the last chunk in every material template
// this editor uses) rather than feeding into `diffuseColor` earlier — a
// projection is a finished, already-graded image (the AI beauty render), not
// a new albedo for the scene's own lights/shadows/AO/tonemapping to
// reprocess; overwriting only the diffuse map would still leave it shaded and
// tonemapped a second time, which is exactly the "should be unlit" look this
// is deliberately avoiding.
//
// `descriptors` is the full visible, z-ordered layer stack (resolveProjectionDescriptors'
// return, in storage order — descriptors[0] is front-most/top-of-panel-list,
// wins wherever it paints). The shader is unrolled per-layer at JS-string-
// template time (N uniquely-named uniform sets per layer, N known at material-
// rebuild time — sceneGraph.js rebuilds the whole graph on every visibility/
// order change) rather than a GLSL uniform array, which sidesteps WebGL1/
// GLSL ES 1.00's dynamic-sampler-array-indexing restrictions entirely. Layers
// composite via Porter-Duff "over" (`mix`), evaluated back-to-front (highest
// array index first) so index 0 blends in last and visually wins overlaps —
// see this module's own file-level ordering convention.
//
// Deliberately declares its own varyings/uniforms rather than reusing
// anything three.js's built-in chunks conditionally define (e.g. `vNormal`,
// the `worldpos_vertex` chunk's `worldPosition`) — those only exist under
// certain feature defines (shadow/env map, mainly), which an unlit
// MeshBasicMaterial mesh doesn't set, so relying on them would break
// projection for exactly that material type. `#include <project_vertex>` and
// `#include <dithering_fragment>` are unconditional anchors present in every
// material's shader template this editor uses.
//
// Every mesh material gets this exact same patch (with the same descriptor
// array) whenever any projection is visible (never a mix of patched/
// unpatched materials of the same shape within one scene-graph build — see
// buildSceneGraph's `projectionDescriptors` option), so there's no risk of
// three.js's WebGLProgram cache silently sharing a compiled program between a
// patched and an unpatched material; no `customProgramCacheKey` override
// needed. A material whose N changes between rebuilds is itself always a
// fresh material instance (sceneGraph.js constructs materials from scratch
// per node per rebuild), so there's no risk of an N-layer program leaking
// into a rebuild with a different N either.
export function applyProjectionsToMaterial(
  material,
  descriptors,
  { flatNormal = null, discardUnpainted = false } = {},
) {
  if (!descriptors?.length) return;

  material.onBeforeCompile = (shader) => {
    if (!shader.fragmentShader.includes("#include <dithering_fragment>"))
      return;

    descriptors.forEach((d, i) => {
      shader.uniforms[`uProjMatrix_${i}`] = { value: d.viewProjectionMatrix };
      shader.uniforms[`uProjCamPos_${i}`] = { value: d.cameraWorldPosition };
      shader.uniforms[`uProjTexture_${i}`] = { value: d.texture };
      shader.uniforms[`uProjDepthTexture_${i}`] = { value: d.depthTexture };
      shader.uniforms[`uProjDepthMaskTexture_${i}`] = {
        value: d.depthMaskTexture,
      };
      shader.uniforms[`uProjTexelSize_${i}`] = { value: d.texelSize };
    });
    if (flatNormal) shader.uniforms.uProjFlatNormal = { value: flatNormal };
    // Shared object references (see their own comments) — NOT `{ value:
    // projectionOutputPassCompensation.value }`/`{ value:
    // projectionEnabled.value }`, which would freeze in today's boolean
    // instead of tracking future toggles. Shared across every layer (there's
    // only one "is a projection-reprocessing pass coming" answer and one
    // "are projections on at all" answer, not one per layer).
    shader.uniforms.uProjCompensateOutputPass =
      projectionOutputPassCompensation;
    shader.uniforms.uProjEnabled = projectionEnabled;

    const vertexUniforms = descriptors
      .map((_, i) => `uniform mat4 uProjMatrix_${i};`)
      .join("\n        ");
    const vertexVaryings = descriptors
      .map((_, i) => `varying vec4 vProjTexCoord_${i};`)
      .join("\n        ");
    const vertexAssigns = descriptors
      .map((_, i) => `vProjTexCoord_${i} = uProjMatrix_${i} * projWorldPos4;`)
      .join("\n        ");

    shader.vertexShader = shader.vertexShader
      .replace(
        "#include <common>",
        `#include <common>
        ${vertexUniforms}
        varying vec3 vProjWorldPos;
        ${vertexVaryings}`,
      )
      .replace(
        "#include <project_vertex>",
        `#include <project_vertex>
        vec4 projWorldPos4 = modelMatrix * vec4(transformed, 1.0);
        vProjWorldPos = projWorldPos4.xyz;
        ${vertexAssigns}`,
      );

    const fragmentUniforms = descriptors
      .map(
        (_, i) => `
        uniform vec3 uProjCamPos_${i};
        uniform sampler2D uProjTexture_${i};
        uniform sampler2D uProjDepthTexture_${i};
        uniform sampler2D uProjDepthMaskTexture_${i};
        uniform vec2 uProjTexelSize_${i};`,
      )
      .join("");
    const fragmentVaryings = descriptors
      .map((_, i) => `varying vec4 vProjTexCoord_${i};`)
      .join("\n        ");

    // One block per layer, evaluated back-to-front (highest index — the
    // back-most/bottom-of-panel-list layer — first) so index 0 (front-most/
    // top-of-panel-list) is mixed in LAST and therefore wins wherever it
    // paints, exactly matching the panel's visual stacking order.
    const layerBlocks = descriptors
      .map((_, i) => i)
      .reverse()
      .map(
        (i) => `
          {
            vec3 projColor = vec3(0.0);
            float coverage = 0.0;
            if (uProjEnabled) {
              vec3 toProjector = normalize(uProjCamPos_${i} - vProjWorldPos);
              float facing = dot(faceNormal, toProjector);
              vec3 projNdc = vProjTexCoord_${i}.xyz / vProjTexCoord_${i}.w;
              // Soft coverage falloff near the projector's own frustum
              // boundary (PROJ_EDGE_FEATHER, a margin in NDC units) and near
              // the facing test's own zero-crossing (PROJ_FACING_FEATHER, a
              // margin in dot-product units) — replaces what used to be a
              // hard in/out cutoff, so a projection's silhouette against
              // real geometry blends rather than cuts.
              float coverageX = 1.0 - smoothstep(1.0 - PROJ_EDGE_FEATHER, 1.0, abs(projNdc.x));
              float coverageY = 1.0 - smoothstep(1.0 - PROJ_EDGE_FEATHER, 1.0, abs(projNdc.y));
              float frustumCoverage = (vProjTexCoord_${i}.w > 0.0) ? coverageX * coverageY : 0.0;
              float facingCoverage = smoothstep(0.0, PROJ_FACING_FEATHER, facing);
              coverage = frustumCoverage * facingCoverage;
              if (coverage > 0.0005) {
                // projNdc.xy is in clip-space NDC range [-1, 1] — remap to
                // the [0, 1] UV range a 2D sampler expects before sampling.
                vec2 projUv = projNdc.xy * 0.5 + 0.5;
                float refDepth = projNdc.z * 0.5 + 0.5;
                // Occlusion — "another object physically blocks this ray from
                // this projector" — is itself still a genuinely binary fact
                // per depth-map texel (see projOcclusionTap), but sampled at a
                // handful of neighboring texels and averaged (see
                // projOcclusionCoverage) rather than just the center one, so
                // the boundary this produces is a soft gradient instead of a
                // hard cutoff exactly one texel wide. Small bias inside each
                // tap avoids a surface self-occluding against its own
                // just-rendered depth.
                coverage *= projOcclusionCoverage(
                  uProjDepthTexture_${i},
                  uProjDepthMaskTexture_${i},
                  projUv,
                  refDepth,
                  uProjTexelSize_${i}
                );
                if (coverage > 0.0005) {
                  vec3 stored = texture2D(uProjTexture_${i}, projUv).rgb;
                  vec3 linearColor = projSrgbToLinear(stored);
                  // Pre-correct only when something downstream will actually
                  // tonemap+encode this frame again (the live viewport's
                  // OutputPass) — renderBeauty's export path wants the plain
                  // linear value, since its own afterward step is just a
                  // linear->sRGB encode with no tonemapping at all.
                  projColor = uProjCompensateOutputPass
                    ? projInverseACESFilmic(linearColor)
                    : linearColor;
                }
              }
            }
            // Porter-Duff "over": full coverage replaces whatever's beneath
            // outright (front-of-stack-wins on overlap), partial/feathered
            // coverage blends softly into it, zero coverage is a no-op.
            projResult = mix(projResult, projColor, coverage);
            projTotalCoverage = projTotalCoverage + coverage * (1.0 - projTotalCoverage);
          }`,
      )
      .join("\n");

    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        `#include <common>
        ${fragmentUniforms}
        uniform bool uProjCompensateOutputPass;
        uniform bool uProjEnabled;
        ${flatNormal ? "uniform vec3 uProjFlatNormal;" : ""}
        varying vec3 vProjWorldPos;
        ${fragmentVaryings}

        // Halved from an initial 0.10 — the first pass read as noticeably
        // softer than wanted.
        const float PROJ_EDGE_FEATHER = 0.05;
        const float PROJ_FACING_FEATHER = 0.15;
        // Radius (in occlusion-map texels) of the PCF kernel below — separate
        // from PROJ_EDGE_FEATHER, since that one softens the projector's own
        // rectangular frustum boundary (an exact, analytic NDC quantity) while
        // this one softens the occlusion silhouette against real geometry (an
        // inherently discrete, texel-sampled one); the two happen to look
        // similar but have unrelated causes and don't need to share a slider.
        // Deliberately wider than "just enough to anti-alias the depth map's
        // own resolution" — one occlusion-map texel covers a fixed fraction of
        // the projector's OWN frustum, which can map to well under one live-
        // camera screen pixel depending on zoom/distance, so a radius that
        // looked plenty soft measured in texels was still imperceptible on
        // screen. But kept modest — this used to be 6.0 with equal-weighted
        // taps (see projOcclusionCoverage's weighting) and that let a
        // fragment that should have stayed fully occluded get pulled toward
        // "painted" by nearby unoccluded neighbors outvoting its own true
        // state, visible as the projected image's background content bleeding
        // onto geometry that should have blocked it.
        const float PROJ_OCCLUSION_FEATHER_TEXELS = 2.0;

        // A single occlusion sample: 1.0 (fully visible to the projector) or
        // 0.0 (blocked by real geometry) at exactly this texel — see
        // renderProjectorDepth for how uProjDepthTexture/uProjDepthMaskTexture
        // get populated, and projOcclusionCoverage below for why this is
        // sampled several times per fragment instead of just once. A texel
        // with nothing recorded at all (open sky, hasRecordedDepth false —
        // see renderProjectorDepth's own comment on why that needs to be a
        // real mask rather than inferred from the depth value alone) counts
        // as fully visible: nothing there to block the projector.
        float projOcclusionTap(
          sampler2D depthTex,
          sampler2D maskTex,
          vec2 uv,
          float refDepth
        ) {
          bool hasRecordedDepth = texture2D(maskTex, uv).a > 0.5;
          bool occluded = hasRecordedDepth
            && refDepth > texture2D(depthTex, uv).r + 0.0015;
          return occluded ? 0.0 : 1.0;
        }

        // Percentage-closer-style softening of the occlusion boundary: a 3x3,
        // CENTER-WEIGHTED ("tent") grid of NEAREST-filtered taps (see
        // ensureProjectorDepthTarget for why these textures are NEAREST, not
        // the usual Linear — averaging several independent binary samples
        // here, rather than letting the GPU's own bilinear filter blend the
        // raw depth *values* together, is the only correct way to soften a
        // discontinuous test: interpolating the inputs to a depth comparison
        // produces a value that's wrong for both sides of the edge it's
        // straddling, not a soft answer). Returns a continuous 0..1 coverage
        // instead of a single sample's hard 0/1.
        //
        // Weighted (center 4x, orthogonal neighbors 2x, corners 1x — the
        // classic tent/binomial-approximation kernel), NOT a flat average:
        // this fragment's own true occlusion state has to dominate the
        // result, or deep inside a region that should be fully occluded, a
        // majority-unoccluded neighborhood (e.g. approaching that region's own
        // edge) would outvote it and let this fragment get painted anyway —
        // exactly the "background content bleeding onto geometry that should
        // have blocked it" failure a flat average produced at a wider radius.
        //
        // Important limitation: this can only ever soften the edge on THIS
        // fragment's own side of it — a GPU fragment shader has no way to
        // blend color into a neighboring pixel that belongs to a *different*
        // mesh's triangle. Concretely: this softens the projection backdrop's
        // own cutout right where real geometry blocks it (the sky fading in
        // outside an object's silhouette), but the object's own edge pixel
        // stays exactly as crisp as ordinary opaque rasterization always is —
        // there is no shader-only way to blur *across* a boundary between two
        // separate objects. A true blur spanning both sides would need a
        // screen-space, depth-edge-aware post-process pass (like the
        // composer's existing GTAOPass), which is a materially bigger change.
        float projOcclusionCoverage(
          sampler2D depthTex,
          sampler2D maskTex,
          vec2 uv,
          float refDepth,
          vec2 texelSize
        ) {
          vec2 r = texelSize * PROJ_OCCLUSION_FEATHER_TEXELS;
          float total = 4.0 * projOcclusionTap(depthTex, maskTex, uv, refDepth);
          total += 2.0 * projOcclusionTap(depthTex, maskTex, uv + vec2(r.x, 0.0), refDepth);
          total += 2.0 * projOcclusionTap(depthTex, maskTex, uv - vec2(r.x, 0.0), refDepth);
          total += 2.0 * projOcclusionTap(depthTex, maskTex, uv + vec2(0.0, r.y), refDepth);
          total += 2.0 * projOcclusionTap(depthTex, maskTex, uv - vec2(0.0, r.y), refDepth);
          total += projOcclusionTap(depthTex, maskTex, uv + vec2(r.x, r.y), refDepth);
          total += projOcclusionTap(depthTex, maskTex, uv + vec2(r.x, -r.y), refDepth);
          total += projOcclusionTap(depthTex, maskTex, uv + vec2(-r.x, r.y), refDepth);
          total += projOcclusionTap(depthTex, maskTex, uv + vec2(-r.x, -r.y), refDepth);
          return total / 16.0;
        }

        // Inverse of the plain sRGB transfer function — turns our texture's
        // raw stored bytes (sampled undecoded, see getProjectionTexture)
        // back into the same linear value three.js's own SRGBToLinear would
        // produce, so we can drive it back through whatever the downstream
        // pass will apply.
        vec3 projSrgbToLinear(vec3 c) {
          return mix(
            c / 12.92,
            pow((c + 0.055) / 1.055, vec3(2.4)),
            step(0.04045, c)
          );
        }

        // Exact inverse of three.js's own ACESFilmicToneMapping (see
        // tonemapping_pars_fragment.glsl.js) at exposure 1 (fixed across
        // this app — see Viewport.jsx) — so that OutputPass's later forward
        // pass reproduces "linearColor" unchanged. Matrices copied verbatim
        // from that chunk; GLSL ES 3.00's built-in inverse(mat3) inverts
        // them, and RRTAndODTFit's rational-quadratic middle step is
        // inverted per-channel via the quadratic formula (RRTAndODTFit is
        // injective over the practically relevant input range, so this has
        // a single well-defined root for real image content).
        vec3 projInverseACESFilmic(vec3 linearColor) {
          const mat3 ACESInputMat = mat3(
            vec3(0.59719, 0.07600, 0.02840),
            vec3(0.35458, 0.90834, 0.13383),
            vec3(0.04823, 0.01566, 0.83777)
          );
          const mat3 ACESOutputMat = mat3(
            vec3(1.60475, -0.10208, -0.00327),
            vec3(-0.53108, 1.10813, -0.07276),
            vec3(-0.07367, -0.00605, 1.07602)
          );

          // Clamped defensively: the rational-quadratic inverse below is
          // only numerically stable for v inside [0, 1] — the matrix
          // transform that produces it doesn't preserve that bound the way
          // the plain RGB clamp above it does, and a component that slips
          // even slightly past 1 pushes A (below) toward zero, blowing the
          // division up into effectively random per-pixel garbage. A
          // genuinely clipped-white source pixel (a bright sun, an
          // overexposed highlight) has no unique inverse anyway — the
          // forward tonemap isn't invertible right at its own white point —
          // so clamping here just picks the boundary answer instead of
          // whatever floating-point noise the unstable math would otherwise
          // return; that's what was showing up as black speckling across
          // solid white regions.
          vec3 v = clamp(inverse(ACESOutputMat) * clamp(linearColor, 0.0, 1.0), 0.0, 1.0);
          vec3 A = 0.983729 * v - 1.0;
          vec3 B = 0.432951 * v - 0.0245786;
          vec3 C = 0.238081 * v + 0.000090537;
          vec3 discriminant = max(B * B - 4.0 * A * C, 0.0);
          vec3 x = (-B - sqrt(discriminant)) / (2.0 * A);
          // Final safety net: even bounded to v's own edge, A can still be a
          // small negative number whose division amplifies whatever noise
          // reaches it — bound the recovered HDR value to a generous but
          // finite range rather than let that reach gl_FragColor as an
          // arbitrarily large (or negative) number.
          return clamp((inverse(ACESInputMat) * x) * 0.6, 0.0, 8.0);
        }`,
      )
      .replace(
        "#include <dithering_fragment>",
        `#include <dithering_fragment>
        {
          // A flat, per-triangle normal from screen-space derivatives of the
          // interpolated world position — same technique three.js's own
          // FLAT_SHADED lighting path uses (normal_fragment_begin.glsl.js),
          // just on world instead of view position. Deliberately NOT the
          // geometry's own vertex normal attribute: an imported GLB may
          // have none at all, and even when it does, a hard-surface/CAD
          // export commonly shares one vertex index across faces meeting at
          // a sharp edge, so a smooth (averaged) vertex normal there doesn't
          // match either face's true direction. A derivative-based flat
          // normal is exact for whatever triangle is actually being
          // rasterized, independent of the source mesh's own vertex-normal
          // data entirely. A fixed geometric property of THIS surface, so
          // it's computed once and reused across every layer's own facing
          // test below rather than per-layer.
          //
          // The projection backdrop (uProjFlatNormal, when set) skips this
          // derivative entirely in favor of its own already-known constant
          // normal instead: it's deliberately placed far from the world
          // origin (see createProjectionBackdrop), so vProjWorldPos there has
          // large magnitude — dFdx/dFdy is a finite difference between
          // adjacent pixels' *interpolated* world positions, and subtracting
          // two nearly-equal large floats to recover a small per-pixel delta
          // is textbook catastrophic cancellation, noisy enough to flip the
          // facing test's sign on scattered individual pixels. A flat plane
          // has one true normal everywhere on its surface anyway, so there
          // was never a reason to re-derive it per pixel for this material.
          vec3 faceNormal = ${flatNormal ? "uProjFlatNormal" : "normalize(cross(dFdx(vProjWorldPos), dFdy(vProjWorldPos)))"};
          vec3 projResult = gl_FragColor.rgb;
          // Accumulated Porter-Duff "over" coverage across every layer —
          // only consumed by discardUnpainted (the projection backdrop):
          // "was this pixel painted by ANY visible projection, and how much."
          float projTotalCoverage = 0.0;
          ${layerBlocks}
          gl_FragColor.rgb = projResult;
          ${
            discardUnpainted
              ? // discardUnpainted (the projection backdrop): rather than a
                // hard boolean discard, the backdrop now carries real alpha
                // (material.transparent = true, see createProjectionBackdrop)
                // so its own feathered edges blend into the real scene
                // background behind it instead of cutting hard. Still
                // discards below a tiny epsilon — a pure GPU cost-skip for
                // the vast unpainted majority of the plane, not a visual
                // mechanism (everything at/above the epsilon blends with its
                // own real alpha). Every other material ignores
                // projTotalCoverage entirely and keeps its own normal
                // fallback appearance (its own color/map/lighting) wherever
                // projResult was never touched — the entire point of a
                // projection being selective.
                "if (projTotalCoverage < 0.003) { discard; } else { gl_FragColor.a = projTotalCoverage; }"
              : ""
          }
        }`,
      );
  };
  material.needsUpdate = true;
}
